import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '.';
import { TaskKind, TrainingCard, TrainingCardData, TrainingType, CardOption } from '../types/TrainingTypes';
import { trainingActionCreators } from './Training';

const interpretSeconds = 5;

interface CombinedState {
    task: string,
    taskAux: string | undefined,
    taskForms: string | undefined,

    solution: string,
    solutionAux: string,
    solutionForms: string,

    example: string | undefined,
    isExampleShown: boolean,

    optionCards: TrainingCard[],
    options: CardOption[],
    chosenOption: CardOption | undefined,
    wrongOption: CardOption | undefined,
    backgrounds: string[],
    shownTime: string | undefined,
    isTimerElapsed: boolean,
    isOptionsShown: boolean,
    interval: number
}

const initialState: CombinedState = {
    task: ' ',
    taskAux: '',
    taskForms: '',

    solution: '',
    solutionAux: '',
    solutionForms: '',

    example: '',
    isExampleShown: false,

    optionCards: [],
    options: [],
    chosenOption: undefined,
    wrongOption: undefined,
    backgrounds: ['', '', '', ''],
    shownTime: undefined,
    isTimerElapsed: false,
    isOptionsShown: false,
    interval: interpretSeconds,
}

const slice = createSlice({
    name: 'combined',
    initialState,
    reducers: {
        setNextCard: (state, action: PayloadAction<{ card: TrainingCard, shownTime: string }>) => {
            const { card, shownTime } = action.payload;

            state.optionCards = [card, ...card.options!];
            state.shownTime = shownTime;
            state.isTimerElapsed = false;
            state.isOptionsShown = false;
            state.isExampleShown = false;

            updateCardParts(state, card, false);
            updateUserChoice(state, card, false)
        },

        setInterval: (state, action: PayloadAction<number>) => {
            state.interval = action.payload;
            if (action.payload === 0 && !state.isOptionsShown)
                state.isTimerElapsed = true;
        },

        showOptions: (state, action: PayloadAction<{ card: TrainingCard, isTimerElapsed: boolean }>) => {
            state.isOptionsShown = true;
            state.isTimerElapsed = action.payload.isTimerElapsed;
            updateCardParts(state, action.payload.card, true)
        },

        chooseOption: (state, action: PayloadAction<{ option: CardOption | undefined, card: TrainingCard }>) => {
            state.chosenOption = action.payload.option;
            updateUserChoice(state, action.payload.card, true)
        },

        resetTimer: (state, action: PayloadAction<string>) => {
            state.shownTime = action.payload;
        },

        showExample: state => {
            state.isExampleShown = true;
        },
    },

    extraReducers: (builder) => {
        builder.addCase('training/reset', (state, action) => {
            return initialState;
        });

        builder.addCase('training/refreshCardData', (state, action) => {
            const data = (action as PayloadAction<TrainingCardData>).payload;
            if (data.card?.trainingType === TrainingType.Combined) {
                updateCardParts(state, data.card!, data.isSolutionShown);
                updateUserChoice(state, data.card!, data.isSolutionShown)
            }
        });
    }
})

function updateCardParts(state: CombinedState, card: TrainingCard, isOptionsShown: boolean) {
    let k = card.modeInfo.modeSetting.taskKind;
    const isCardDirect = card.modeInfo.isDirect;

    const isPhraseFirst = !card.pronunciation || k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation || k === TaskKind.PhraseForms || k === TaskKind.Phrase;
    const isIPronunciationFirst = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase || k === TaskKind.PronunciationForms || k === TaskKind.Pronunciation)

    const isPhraseSecond = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase);
    const isPronunciationaSecond = card.pronunciation && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation);

    const isFormsThird = card.forms && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhraseForms || k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationForms)

    state.options = state.optionCards
        .map(vc => {
            const first = isIPronunciationFirst ? vc.pronunciation : vc.phrase;
            const second = isPhraseSecond ? vc.phrase : isPronunciationaSecond ? vc.pronunciation : '';
            const third = isFormsThird ? vc.forms : '';

            const content = isCardDirect ? vc.meaning : first;
            const contentAux = isCardDirect ? '' : second;
            const contentForms = isCardDirect ? '' : third;

            const wrongOption = isCardDirect ? vc.phrase : vc.meaning;
            const width = getTextWidth(content, 20) + getTextWidth(contentAux, 15);
            return {
                content,
                contentAux,
                contentForms,
                wrongOption,
                card: vc,
                width
            };
        })
        .sort((a, b) => a.width - b.width);

    const first = isIPronunciationFirst ? card.pronunciation : card.phrase;
    const second = isPhraseSecond ? card.phrase : isPronunciationaSecond ? card.pronunciation : '';
    const third = isFormsThird ? card.forms : '';

    state.task = isCardDirect ? first : card.meaning;
    state.taskAux = isCardDirect ? second : '';
    state.taskForms = isCardDirect ? third : '';

    state.solution = isCardDirect ? card.meaning : first;
    state.solutionAux = isCardDirect ? '' : second;
    state.solutionForms = isCardDirect ? '' : third;

    state.example = card.example;
}

function updateUserChoice(state: CombinedState, card: TrainingCard, isSolutionShown: boolean) {
    if (isSolutionShown) {
        const backgrounds = ['', '', '', ''];

        const chosenIndex = state.chosenOption ? state.options.findIndex(x => x.card.id === state.chosenOption?.card.id) : -1;
        const isCorrect = state.solution === state.chosenOption?.content;

        if (isCorrect)
            backgrounds[chosenIndex] = 'ch-green';
        else {
            const correctIndex = state.options.findIndex(x => x.card.id === card.id);
            backgrounds[correctIndex] = 'ch-green';
            backgrounds[chosenIndex === -1 ? backgrounds.length : chosenIndex] = 'ch-red';
        }

        state.backgrounds = backgrounds;
        state.wrongOption = !isCorrect && chosenIndex !== -1 ? state.options[chosenIndex] : undefined;
    }
    else {
        state.backgrounds = ['', '', '', ''];
        state.wrongOption = undefined;
    }
}

function getTextWidth(text: string, size: number) {
    const canvas = (getTextWidth as any).canvas || ((getTextWidth as any).canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = `bold ${size}pt arial`;
    const metrics = context.measureText(text);
    return metrics.width as number;
}

const setNextCard = (card: TrainingCard) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.setNextCard({ card: card, shownTime: new Date().toISOString() }));

        let seconds = interpretSeconds;
        dispatch(slice.actions.setInterval(seconds));
        const intervalId = setInterval(() => {
            seconds--;
            if (getState().training.card?.id === card!.id)
                dispatch(slice.actions.setInterval(seconds));

            if (seconds === 0)
                clearInterval(intervalId);
        }, 1000);
    }
}

const showOptions = (isTimerElapsed: boolean) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;
        dispatch(slice.actions.showOptions({ card: training.card!, isTimerElapsed }));
    }
}

const chooseOption = (option: CardOption | undefined) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;
        const state = getState().combined;

        const isMistake = option?.content !== state.solution;
        dispatch(trainingActionCreators.showSolution(isMistake));
        dispatch(slice.actions.chooseOption({ option, card: training.card! }));
    }
};

const resetTimer = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.resetTimer(new Date().toISOString()));
    }
}

export const combinedReducer = slice.reducer;

export const combinedActionCreators = {
    setNextCard,
    showOptions,
    chooseOption,
    resetTimer,
    showExample: slice.actions.showExample,
}
